import { Modal, Spin, Table, Typography } from 'antd';
import {
  useGetDonorDonationHistoryQuery,
  useGetDonorNotesQuery,
} from 'gql/types';
import { IDonorRecord } from 'modules/dashboard/types/donor';
import { FC, useMemo } from 'react';

import {
  DonationStatus,
  DonationType,
  getDonationHistoryColumns,
  IDonationHistoryRow,
} from './donationHistoryColumns';
import { IDonorNoteRow, noteColumns } from './noteColumns';

import styles from '../../../OperationsDashboard/ops-theme.module.css';

const { Text } = Typography;

interface IDonorDonationHistoryModalProps {
  donor: IDonorRecord | null;
  visible: boolean;
  onClose: () => void;
  periodStart?: Date;
  periodEnd?: Date;
  isDarkMode?: boolean;
}

export const DonorDonationHistoryModal: FC<IDonorDonationHistoryModalProps> = ({
  donor,
  visible,
  onClose,
  periodStart,
  periodEnd,
  isDarkMode,
}) => {
  const { data, loading } = useGetDonorDonationHistoryQuery({
    skip: !donor,
    variables: { input: { donorId: donor?.donorId || '' } },
  });

  const { data: notesData, loading: notesLoading } = useGetDonorNotesQuery({
    skip: !donor,
    variables: { donorId: donor?.donorId || '' },
  });

  const notes: IDonorNoteRow[] = useMemo(() => {
    return (notesData?.getDonorNotes ?? []).map((n) => ({
      id: n.id,
      createdAt: n.createdAt,
      author: `${n.user.firstName} ${n.user.lastName}`,
      isPinned: n.isPinned,
      text: n.text,
      fileCount: n.files?.length ?? 0,
    }));
  }, [notesData]);

  const donations: IDonationHistoryRow[] = useMemo(() => {
    return (data?.getDonorDonationHistory?.donations ?? []).map((d) => ({
      id: d.id,
      shortId: d.shortId,
      date: d.date,
      donorName: d.donorName,
      locationName: d.locationName,
      regionName: d.regionName,
      type: d.type as DonationType,
      approvedML: d.approvedML,
      status: d.status as DonationStatus,
    }));
  }, [data]);

  const columns = useMemo(
    () => getDonationHistoryColumns(donations, periodStart, periodEnd),
    [donations, periodStart, periodEnd]
  );

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      wrapClassName={
        isDarkMode ? styles.opsMetricModal : styles.opsMetricModalLight
      }
      bodyStyle={{
        background: isDarkMode ? '#0C0612' : '#FFFFFF',
        padding: '16px 20px',
      }}
      title={
        donor ? (
          <div>
            <span>
              {donor.donorName} — Donation History (
              {data?.getDonorDonationHistory?.total ?? '...'})
            </span>
            <div>
              <Text
                type="secondary"
                style={{ fontSize: 12, fontWeight: 'normal' }}
              >
                All-time donations for this donor
              </Text>
            </div>
          </div>
        ) : null
      }
      width={900}
      destroyOnClose
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={donations}
          scroll={{ x: 800 }}
          pagination={{
            defaultPageSize: 10,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} donations`,
          }}
          size="small"
        />
      )}
      <div style={{ marginTop: 24 }}>
        <Text strong style={{ fontSize: 14 }}>
          Donor Notes ({notesLoading ? '…' : notes.length})
        </Text>
        {notesLoading ? (
          <div
            style={{ display: 'flex', justifyContent: 'center', padding: 24 }}
          >
            <Spin />
          </div>
        ) : (
          <Table
            rowKey="id"
            columns={noteColumns}
            dataSource={notes}
            style={{ marginTop: 8 }}
            pagination={{
              defaultPageSize: 5,
              pageSizeOptions: ['5', '10', '20'],
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} notes`,
            }}
            size="small"
            locale={{ emptyText: 'No notes for this donor' }}
          />
        )}
      </div>
    </Modal>
  );
};

export default DonorDonationHistoryModal;
